/** @file Settings › API tokens data: list, create (keeps the one-time secret), revoke; one change at a time. */
import type { Scope } from '@pose2d/api/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SettingsApi } from '../../api/createSettingsApi';
import type { CreatedToken, TokenInfo } from '../../api/sessionTypes';

/** State and actions for TokensSection; a finished create or revoke reloads the list. */
export function useTokens(client: Pick<SettingsApi, 'listTokens' | 'createToken' | 'revokeToken'>) {
  const [tokens, setTokens] = useState<TokenInfo[] | null>(null);
  const [created, setCreated] = useState<CreatedToken | null>(null);
  const [failure, setFailure] = useState<{ thrown: unknown } | null>(null);
  const [busy, setBusy] = useState(false);
  const inflight = useRef(false);
  const fail = useCallback((thrown: unknown) => { setFailure({ thrown }); }, []);
  const reload = useCallback(() => {
    client.listTokens().then((r) => { setTokens(r.tokens); }, fail);
  }, [client, fail]);
  useEffect(reload, [reload]);
  const run = (start: () => Promise<void>): void => {
    if (inflight.current) return;
    inflight.current = true;
    setBusy(true);
    setFailure(null);
    start().then(reload, fail).finally(() => { inflight.current = false; setBusy(false); });
  };
  const create = (name: string, scopes: Scope[]): void => { run(async () => { setCreated(await client.createToken(name, scopes)); }); };
  const revoke = (id: string): void => { run(async () => { await client.revokeToken(id); }); };
  return { tokens, created, failure, busy, create, revoke, dismissCreated: () => { setCreated(null); } };
}
